/**
 * BML Analytics Tracker — singleton module.
 *
 * Responsibilities:
 *  - Manage visitor (bml_vid) and session (bml_sid) cookies.
 *  - Collect pageview dwell time, tracking events, and user identity.
 *  - Batch-flush to POST /api/track/ingest every 5s or when queue reaches 20 events.
 *  - Beacon on visibilitychange:hidden / pagehide.
 *  - Retry on failure with exponential backoff; fall back to localStorage queue.
 *  - Pause dwell timer while document is hidden.
 *  - Honor window.bmlAnalyticsOptOut === true.
 */

const INGEST_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/track/ingest`;
const COOKIE_VID = 'bml_vid';
const COOKIE_SID = 'bml_sid';
const VID_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds
const SID_MAX_AGE = 30 * 60; // 30 minutes in seconds
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 20;
const LS_QUEUE_KEY = 'bml_analytics_queue';
const MAX_BACKOFF_MS = 60000;

// ─── Cookie helpers ──────────────────────────────────────────────────────────

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name, value, maxAge) {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
}

function deleteCookie(name) {
  document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
}

// ─── UUID helper ─────────────────────────────────────────────────────────────

function uuid() {
  if (crypto && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ─── UTM / referrer capture ──────────────────────────────────────────────────

function captureUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v;
    });
    return utm;
  } catch {
    return {};
  }
}

// ─── Singleton state ─────────────────────────────────────────────────────────

let initialized = false;
let queue = [];
let flushTimer = null;
let backoffMs = 2000;
let backoffTimer = null;

let visitorId = null;
let sessionId = null;
let sessionStartTs = null; // epoch ms when current session cookie was set

let userId = null;
let userRole = null;

let currentPagePath = null;
let currentPageTitle = null; // title captured on pageview(), emitted on end
let currentPageStartTs = null; // epoch ms when page was entered
let hiddenSinceTs = null; // epoch ms when page became hidden
let accumulatedHiddenMs = 0; // ms to subtract from dwell

let pageviewRef = null; // used externally to guard StrictMode double-fire

let context = {}; // screen, lang, tz, referrer, utm — captured once on init

// ─── Session management ───────────────────────────────────────────────────────

function ensureSession() {
  const existing = getCookie(COOKIE_SID);
  const now = Date.now();
  if (existing) {
    sessionId = existing;
    // Refresh rolling expiry
    setCookie(COOKIE_SID, existing, SID_MAX_AGE);
    if (!sessionStartTs) sessionStartTs = now;
  } else {
    sessionId = uuid();
    sessionStartTs = now;
    setCookie(COOKIE_SID, sessionId, SID_MAX_AGE);
  }
}

function rotateSessionIfIdle() {
  // If session cookie is gone (30-min inactivity), create new session
  const existing = getCookie(COOKIE_SID);
  if (!existing) {
    sessionId = uuid();
    sessionStartTs = Date.now();
    setCookie(COOKIE_SID, sessionId, SID_MAX_AGE);
  } else {
    // Refresh rolling expiry
    setCookie(COOKIE_SID, existing, SID_MAX_AGE);
  }
}

// ─── Queue / flush ─────────────────────────────────────────────────────────────

function loadQueueFromStorage() {
  try {
    const raw = localStorage.getItem(LS_QUEUE_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      if (Array.isArray(saved) && saved.length) {
        queue = [...saved, ...queue];
        localStorage.removeItem(LS_QUEUE_KEY);
      }
    }
  } catch {
    // ignore
  }
}

function persistQueueToStorage(events) {
  try {
    const existing = JSON.parse(localStorage.getItem(LS_QUEUE_KEY) || '[]');
    localStorage.setItem(LS_QUEUE_KEY, JSON.stringify([...existing, ...events]));
  } catch {
    // ignore
  }
}

function buildPayload(events) {
  return {
    visitorId,
    sessionId,
    context: {
      path:     window.location.pathname,
      title:    document.title || null,
      referrer: document.referrer || null,
      screen:   { w: window.screen.width, h: window.screen.height },
      lang:     navigator.language || null,
      tz:       Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      utm: {
        source:   context.utm_source   || null,
        medium:   context.utm_medium   || null,
        campaign: context.utm_campaign || null,
        term:     context.utm_term     || null,
        content:  context.utm_content  || null,
      },
    },
    events,
  };
}

async function sendBeacon(events) {
  if (!events.length) return;
  try {
    const blob = new Blob([JSON.stringify(buildPayload(events))], { type: 'application/json' });
    navigator.sendBeacon(INGEST_URL, blob);
  } catch {
    persistQueueToStorage(events);
  }
}

async function flushQueue(useBeacon = false) {
  if (window.bmlAnalyticsOptOut === true) {
    queue = [];
    return;
  }
  if (!queue.length) return;

  const batch = queue.splice(0, FLUSH_BATCH_SIZE);

  if (useBeacon) {
    await sendBeacon(batch);
    return;
  }

  const token = localStorage.getItem('accessToken') || null;

  try {
    const resp = await fetch(INGEST_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(buildPayload(batch)),
      keepalive: true,
    });

    if (resp.status === 429) {
      // Re-queue and back off
      queue = [...batch, ...queue];
      backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
      clearInterval(flushTimer);
      backoffTimer = setTimeout(() => {
        backoffMs = 2000;
        startFlushTimer();
      }, backoffMs);
      return;
    }

    if (!resp.ok) {
      persistQueueToStorage(batch);
    }
  } catch {
    persistQueueToStorage(batch);
  }
}

function startFlushTimer() {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = setInterval(() => flushQueue(), FLUSH_INTERVAL_MS);
}

// ─── Enqueue ──────────────────────────────────────────────────────────────────

function enqueue(name, props = {}, value = null) {
  if (window.bmlAnalyticsOptOut === true) return;

  rotateSessionIfIdle();

  // 'pageview' is the built-in tracker pageview type; everything else is 'event'
  const type = name === 'pageview' || name === 'dwell' ? 'pageview' : 'event';

  const event = {
    type,
    name,
    path: window.location.pathname,
    started_at: new Date().toISOString(),
    props: props || {},
    ...(value != null ? { value } : {}),
  };

  queue.push(event);

  if (queue.length >= FLUSH_BATCH_SIZE) {
    flushQueue();
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * init() — call once at app startup.
 * Idempotent: safe to call multiple times (StrictMode double-invoke).
 */
export function init() {
  if (initialized) return;
  initialized = true;

  if (window.bmlAnalyticsOptOut === true) return;

  // Visitor cookie (1 year)
  visitorId = getCookie(COOKIE_VID);
  if (!visitorId) {
    visitorId = uuid();
    setCookie(COOKIE_VID, visitorId, VID_MAX_AGE);
  }

  // Session cookie (30 min rolling)
  ensureSession();

  // Capture one-time context
  context = {
    screen: `${window.screen.width}x${window.screen.height}`,
    lang: navigator.language || null,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    referrer: document.referrer || null,
    ...captureUtm(),
  };

  // Load any events persisted from a prior failed flush
  loadQueueFromStorage();

  // Start periodic flush
  startFlushTimer();

  // Beacon on page hide
  const onHide = () => {
    endCurrentPageview();
    flushQueue(true);
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenSinceTs = Date.now();
      onHide();
    } else {
      // Page became visible again — resume dwell
      if (hiddenSinceTs != null) {
        accumulatedHiddenMs += Date.now() - hiddenSinceTs;
        hiddenSinceTs = null;
      }
    }
  });
  window.addEventListener('pagehide', onHide);
}

/**
 * pageview(path, title) — call on every route change.
 * Ends the previous pageview (records dwell), starts new in-memory only.
 * We DO NOT emit a "start" event — only the completed (ended) pageview is
 * pushed by endCurrentPageview() on navigation or pagehide/visibilitychange.
 * This prevents duplicate pageview rows (one start + one end) in the DB.
 */
export function pageview(path, title) {
  if (window.bmlAnalyticsOptOut === true) return;

  endCurrentPageview();

  currentPagePath = path;
  currentPageStartTs = Date.now();
  accumulatedHiddenMs = 0;
  hiddenSinceTs = null;
  // Keep session cookie alive even without an enqueue.
  rotateSessionIfIdle();
  // Remember title for when this pageview ends.
  currentPageTitle = title || document.title || null;
}

/**
 * Ends the current pageview by computing dwell time and enqueuing ONE completed
 * pageview event (with started_at, ended_at, dwell_ms). Called automatically
 * before each new pageview and on page hide. This is the ONLY place pageview
 * rows are pushed to the queue — pageview() itself does not emit a start event.
 */
function endCurrentPageview() {
  if (!currentPageStartTs) return;

  const now = Date.now();
  const hiddenMs = hiddenSinceTs != null
    ? accumulatedHiddenMs + (now - hiddenSinceTs)
    : accumulatedHiddenMs;

  const dwellMs = Math.max(0, now - currentPageStartTs - hiddenMs);

  if (dwellMs > 500) {
    // Push directly to avoid wrapping dwell_ms inside props
    rotateSessionIfIdle();
    queue.push({
      type:       'pageview',
      name:       'pageview',
      path:       currentPagePath,
      title:      currentPageTitle,
      dwell_ms:   dwellMs,
      started_at: new Date(currentPageStartTs || Date.now()).toISOString(),
      ended_at:   new Date().toISOString(),
      props:      {},
    });
    if (queue.length >= FLUSH_BATCH_SIZE) flushQueue();
  }

  currentPagePath = null;
  currentPageTitle = null;
  currentPageStartTs = null;
  accumulatedHiddenMs = 0;
  hiddenSinceTs = null;
}

/**
 * track(name, props, value?) — enqueue a named event.
 */
export function track(name, props = {}, value = null) {
  if (window.bmlAnalyticsOptOut === true) return;
  enqueue(name, props, value);
}

/**
 * identify(userId, role) — stamp subsequent events with user context.
 */
export function identify(id, role) {
  userId = id || null;
  userRole = role || null;
}

/**
 * optOut() — clear cookies, queue, and set opt-out flag.
 */
export function optOut() {
  window.bmlAnalyticsOptOut = true;
  queue = [];
  clearInterval(flushTimer);
  clearTimeout(backoffTimer);
  deleteCookie(COOKIE_VID);
  deleteCookie(COOKIE_SID);
  try { localStorage.removeItem(LS_QUEUE_KEY); } catch { /* ignore */ }
  initialized = false;
}

/**
 * getPageviewRef() — returns a ref-like object for StrictMode guard in AnalyticsProvider.
 * The provider uses this to avoid double-emitting the same pageview.
 */
export function getPageviewRef() {
  return pageviewRef;
}
export function setPageviewRef(ref) {
  pageviewRef = ref;
}
